/**
 * Custom Hook: useCache
 * Skill: pagination-tinvaz.skill.md
 * 
 * Hook para gestionar caché con localStorage y Socket.io
 * 
 * @module hooks/useCache
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';

/**
 * Hook para gestión de caché con localStorage y Socket.io
 * 
 * @param {Object} options - Configuración
 * @param {string} options.namespace - Prefijo para keys en localStorage
 * @param {number} [options.ttl=1200000] - Tiempo de vida en ms (20 min default)
 * @param {string} [options.socketEvent] - Evento Socket.io para invalidación
 * @param {boolean} [options.enabled=true] - Habilitar cache
 * 
 * @returns {Object} Funciones de control del caché
 */
export const useCache = ({
  namespace = 'cache',
  ttl = 1200000, // 20 minutos
  socketEvent,
  enabled = true
} = {}) => {
  const [pendingUpdates, setPendingUpdates] = useState(0);
  const socketRef = useRef(null);

  /**
   * Generar key de cache a partir de un objeto
   */
  const generateCacheKey = useCallback((keyObject) => {
    // Ordenar keys para consistencia
    const sortedKey = Object.keys(keyObject)
      .sort()
      .reduce((acc, key) => {
        acc[key] = keyObject[key];
        return acc;
      }, {});
    
    // Convertir a string
    const keyString = JSON.stringify(sortedKey);
    return `${namespace}:${keyString}`;
  }, [namespace]);

  /**
   * Guardar en caché
   */
  const setCached = useCallback((data, keyObject) => {
    if (!enabled) return;
    
    try {
      const cacheKey = generateCacheKey(keyObject);
      const cacheData = {
        data,
        timestamp: Date.now(),
        ttl
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (err) {
      console.warn('Error guardando en caché:', err);
      // Si localStorage está lleno, limpiar caché antiguo
      if (err.name === 'QuotaExceededError') {
        clearCache();
      }
    }
  }, [enabled, generateCacheKey, ttl]);

  /**
   * Obtener de caché
   */
  const getCached = useCallback((keyObject) => {
    if (!enabled) return null;
    
    try {
      const cacheKey = generateCacheKey(keyObject);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;
      
      const cacheData = JSON.parse(cached);
      const now = Date.now();
      
      // Verificar si expiró
      if (now - cacheData.timestamp > cacheData.ttl) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return cacheData.data;
    } catch (err) {
      console.warn('Error leyendo caché:', err);
      return null;
    }
  }, [enabled, generateCacheKey]);

  /**
   * Limpiar caché completo del namespace
   */
  const clearCache = useCallback(() => {
    if (!enabled) return;
    
    try {
      const keys = Object.keys(localStorage);
      const namespaceKeys = keys.filter(key => key.startsWith(`${namespace}:`));
      
      namespaceKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      console.log(`🗑️ Caché limpiado: ${namespaceKeys.length} items del namespace "${namespace}"`);
    } catch (err) {
      console.warn('Error limpiando caché:', err);
    }
  }, [enabled, namespace]);

  /**
   * Limpiar caché expirado
   */
  const clearExpiredCache = useCallback(() => {
    if (!enabled) return;
    
    try {
      const keys = Object.keys(localStorage);
      const namespaceKeys = keys.filter(key => key.startsWith(`${namespace}:`));
      const now = Date.now();
      let removedCount = 0;
      
      namespaceKeys.forEach(key => {
        try {
          const cached = localStorage.getItem(key);
          if (!cached) return;
          
          const cacheData = JSON.parse(cached);
          if (now - cacheData.timestamp > cacheData.ttl) {
            localStorage.removeItem(key);
            removedCount++;
          }
        } catch (err) {
          // Si hay error parseando, eliminar
          localStorage.removeItem(key);
          removedCount++;
        }
      });
      
      if (removedCount > 0) {
        console.log(`🧹 Caché expirado limpiado: ${removedCount} items`);
      }
    } catch (err) {
      console.warn('Error limpiando caché expirado:', err);
    }
  }, [enabled, namespace]);

  /**
   * Limpiar contador de actualizaciones pendientes
   */
  const clearPendingUpdates = useCallback(() => {
    setPendingUpdates(0);
  }, []);

  /**
   * Efecto para configurar Socket.io (si está habilitado)
   */
  useEffect(() => {
    if (!enabled || !socketEvent) return;
    
    // Conectar a Socket.io
    try {
      const socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:8080';
      socketRef.current = io(socketUrl, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5
      });
      
      // Listener para el evento
      socketRef.current.on(socketEvent, (data) => {
        console.log(`🔔 Socket.io: ${socketEvent}`, data);
        setPendingUpdates(prev => prev + 1);
      });
      
      // Listeners de conexión
      socketRef.current.on('connect', () => {
        console.log('✅ Socket.io conectado');
      });
      
      socketRef.current.on('disconnect', () => {
        console.log('❌ Socket.io desconectado');
      });
      
      socketRef.current.on('error', (err) => {
        console.error('❌ Socket.io error:', err);
      });
      
    } catch (err) {
      console.warn('Error configurando Socket.io:', err);
    }
    
    // Cleanup
    return () => {
      if (socketRef.current) {
        socketRef.current.off(socketEvent);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [enabled, socketEvent]);

  /**
   * Efecto para limpiar caché expirado periódicamente
   */
  useEffect(() => {
    if (!enabled) return;
    
    // Limpiar al montar
    clearExpiredCache();
    
    // Limpiar cada 5 minutos
    const interval = setInterval(clearExpiredCache, 300000);
    
    return () => clearInterval(interval);
  }, [enabled, clearExpiredCache]);

  return {
    getCached,
    setCached,
    clearCache,
    clearExpiredCache,
    pendingUpdates,
    clearPendingUpdates
  };
};

export default useCache;
